import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { usePageMedia } from "../../lib/usePageMedia";
import { usePageTexts } from "../../lib/usePageTexts";

const RDV_TYPES = [
  { id: "decouverte", label: "Découverte" },
  { id: "coordination", label: "Coordination mariage" },
  { id: "location", label: "Location & décoration" },
  { id: "telephone_visio", label: "Téléphone / visio" },
];

export default function RendezVousPage() {
  const [type, setType] = useState(RDV_TYPES[0].id);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const pageMedia = usePageMedia();
  const { text } = usePageTexts();
  const hero = pageMedia["rendez-vous:hero"];

  const submit = async (e) => {
    e.preventDefault();
    const v = (id) => document.getElementById(id)?.value?.trim() || "";
    setSending(true);
    try {
      await api.post("/appointments", {
        type,
        date: v("rdv-date"),
        slot: v("rdv-creneau"),
        name: v("rdv-nom"),
        email: v("rdv-email"),
        message: v("rdv-message") || null,
      });
      setSent(true);
      toast.success("Demande de rendez-vous envoyée — Art Event vous confirmera le créneau");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="container-ae max-w-3xl py-12 md:py-16" data-testid="appointment-page">
      <p className="overline-ae mb-2">Prendre rendez-vous</p>
      <h1 className="text-3xl sm:text-4xl text-deepgreen" data-testid="appointment-title">Échanger avec Art Event</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {text("rendez-vous:intro", "Créneaux du lundi au samedi, 9 h – 18 h. Un rendez-vous est obligatoire avant tout engagement de coordination.")}
      </p>
      {hero && (
        <img src={hero.url} alt={hero.alt || "Rendez-vous Art Event"} className="mt-6 aspect-[21/9] w-full rounded-xl object-cover" data-testid="page-hero-image" />
      )}

      <form className="card-ae mt-8 space-y-5 p-6 md:p-8" onSubmit={submit} data-testid="appointment-form">
        <div>
          <label className="label-ae">Type de rendez-vous</label>
          <div className="grid gap-2 sm:grid-cols-2" data-testid="appointment-types">
            {RDV_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setType(t.id)}
                data-testid={`rdv-type-${t.id}`}
                className={`rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors ${type === t.id ? "border-deepgreen bg-sage-light text-deepgreen" : "border-border bg-white text-foreground/70 hover:border-sage"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="label-ae" htmlFor="rdv-date">Date souhaitée</label><input id="rdv-date" type="date" className="input-ae" data-testid="rdv-date" /></div>
          <div><label className="label-ae" htmlFor="rdv-creneau">Créneau</label>
            <select id="rdv-creneau" className="input-ae" data-testid="rdv-slot">
              <option>9 h – 11 h</option><option>11 h – 13 h</option><option>14 h – 16 h</option><option>16 h – 18 h</option>
            </select>
          </div>
          <div><label className="label-ae" htmlFor="rdv-nom">Votre nom</label><input id="rdv-nom" className="input-ae" data-testid="rdv-name" /></div>
          <div><label className="label-ae" htmlFor="rdv-email">Votre e-mail</label><input id="rdv-email" type="email" className="input-ae" data-testid="rdv-email" /></div>
        </div>
        <div>
          <label className="label-ae" htmlFor="rdv-message">Votre projet en quelques mots</label>
          <textarea id="rdv-message" className="input-ae min-h-28" data-testid="rdv-message" placeholder="Date envisagée, lieu, nombre d'invités…" />
        </div>
        <button type="submit" disabled={sending || sent} className="btn-primary-ae disabled:opacity-50" data-testid="rdv-submit">
          <CalendarDays className="h-4 w-4" /> {sent ? "Demande envoyée — en attente de confirmation" : sending ? "Envoi…" : "Demander ce rendez-vous"}
        </button>
      </form>
    </div>
  );
}
